/**
 * Operate-UI client for the summarize endpoint behind the "Summarize"
 * button in the content picker:
 *
 *   POST /api/v1/content/summarize   {objectId | searchPath, maxItems?}
 *
 * The backend reads the object's live spec over the SOAP SDK, distills
 * it, and narrates it into markdown when the LLM gateway is enabled.
 * Degrades gracefully: with the gateway off it still returns the
 * distilled STRUCTURE (markdown empty), so the caller renders a
 * structured fallback.
 */
import { AxiosError } from "axios";
import { api } from "./api";

export interface DistilledSection {
  name:          string;
  type:          string;
  visualization: string | null;
  itemCount:     number;
  dataItems:     string[];
}

export interface DistilledSummary {
  kind:           string;
  name:           string | null;
  specFormat:     string;
  specLength:     number;
  dataSources:    string[];
  visualizations: string[];
  dataItems:      string[];
  filters:        string[];
  prompts:        string[];
  sections:       DistilledSection[];
  notes:          string[];
}

export interface SummarizeResult {
  found:              boolean;
  markdown:           string;
  narrativeAvailable: boolean;
  distilled:          DistilledSummary | null;
  note?:              string;
}

export interface SummarizeApiError {
  code:   string;
  detail: string;
  status: number;
}

/** Summarize a Cognos object by store-ID (preferred) or search-path. */
export async function summarizeObject(ref: {
  objectId?:  string;
  searchPath?: string;
  maxItems?:  number;
}): Promise<SummarizeResult> {
  try {
    const { data } = await api.post<{
      found?:              boolean;
      markdown?:           string;
      narrativeAvailable?: boolean;
      distilled?:          DistilledSummary | null;
      note?:               string;
    }>("/content/summarize", {
      objectId:   ref.objectId,
      searchPath: ref.searchPath,
      maxItems:   ref.maxItems,
    });
    return {
      found:              data?.found === true,
      markdown:           typeof data?.markdown === "string" ? data.markdown : "",
      narrativeAvailable: data?.narrativeAvailable === true,
      distilled:          (data?.distilled ?? null) as DistilledSummary | null,
      note:               typeof data?.note === "string" ? data.note : undefined,
    };
  } catch (e) {
    if (e instanceof AxiosError) {
      const body = e.response?.data as { error?: string; detail?: string } | undefined;
      const err: SummarizeApiError = {
        code:   typeof body?.error  === "string" ? body.error  : "http.status",
        detail: typeof body?.detail === "string" ? body.detail : (e.message ?? `HTTP ${e.response?.status}`),
        status: e.response?.status ?? 0,
      };
      throw err;
    }
    throw e;
  }
}

/**
 * Render a distilled summary as markdown — the structured fallback shown
 * when the LLM gateway is off (or narration failed).
 */
export function renderDistilledMarkdown(d: DistilledSummary): string {
  const lines: string[] = [];
  lines.push(`**${d.name ?? "(unnamed)"}** — ${d.kind || "object"}`);
  lines.push("");
  const section = (label: string, xs: string[]) => {
    if (xs && xs.length > 0) lines.push(`**${label}:** ${xs.join(", ")}`);
  };
  section("Data sources", d.dataSources);
  section("Visualizations", d.visualizations);
  section("Data items", d.dataItems);
  section("Filters", d.filters);
  section("Prompts", d.prompts);
  if (d.sections && d.sections.length > 0) {
    lines.push("");
    lines.push(d.kind === "report" ? "**Queries:**" : "**Widgets:**");
    for (const s of d.sections) {
      const viz = s.visualization ? ` _(${s.visualization})_` : "";
      const items = s.dataItems && s.dataItems.length > 0 ? `: ${s.dataItems.join(", ")}` : "";
      lines.push(`- **${s.name}**${viz}${items}`);
    }
  }
  return lines.join("\n");
}

/** Cognos classes the "Summarize" button is offered for. */
export function isSummarizable(objectClass: string | undefined | null): boolean {
  if (!objectClass) return false;
  const c = objectClass.toLowerCase().replace(/^_/, "");
  return (
    // report family
    c === "report" ||
    c === "reportview" ||
    c === "query" ||
    c === "querystudioreport" ||
    c === "analysis" ||
    c === "interactivereport" ||
    // dashboard / exploration family
    c === "dashboard" ||
    c === "exploration" ||
    c === "explorationview" ||
    // model family (data modules + FM packages)
    c === "module" ||
    c === "datamodule" ||
    c === "basemodule" ||
    c === "package" ||
    c === "model"
  );
}
